"use client";

import { useState } from "react";
import Image from "next/image";
import { ArrowRight, Armchair, Star } from "lucide-react";
import { useTranslations } from "next-intl";

import { AppAssets } from "@/lib/assets";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { formatRupiah } from "@/lib/mock/data";
import type { TicketingZone } from "@/lib/ticketing";

/** Empat posisi visual peta stadion — cocok ke zona asli lewat `code`nya. */
const SECTOR_POSITIONS = ["utara", "vip-barat", "ekonomi-timur", "selatan"] as const;

function availableCount(zone: TicketingZone): number {
  return zone.seats.filter((s) => s.status === "available").length;
}

/** Peta stadion abstrak — sektor sebagai tombol, bukan gambar. Data zona asli dari Ticketing Service. */
export function StadiumMap({ fixtureId, zones }: { fixtureId: string; zones: TicketingZone[] }) {
  const t = useTranslations("Tiket");
  const firstAvailable = zones.find((z) => availableCount(z) > 0) ?? zones[0];
  const [selectedCode, setSelectedCode] = useState(firstAvailable?.code ?? SECTOR_POSITIONS[1]);
  const zone = zones.find((z) => z.code === selectedCode);
  const zoneAvailable = zone ? availableCount(zone) : 0;
  const soldOut = zone ? zoneAvailable === 0 : true;

  return (
    <>
      <section className="relative flex aspect-4/5 w-full flex-col items-center justify-center overflow-hidden rounded-2xl border border-white/5 bg-surface-elevated p-4">
        <Image
          src={AppAssets.stadiumMap}
          alt=""
          fill
          sizes="(max-width: 512px) 100vw, 512px"
          className="pointer-events-none object-contain opacity-15"
        />
        <div className="relative mb-6 text-center">
          <h2 className="font-label text-label-caps uppercase text-on-surface-variant">
            Stadion Segiri
          </h2>
          <p className="mt-1 text-body-sm text-on-surface-variant">{t("kapasitas")}</p>
        </div>

        <div className="relative flex aspect-3/4 w-full max-w-[280px] flex-col items-center justify-between">
          <Sector
            code="utara"
            label={t("sectors.utara")}
            zones={zones}
            selected={selectedCode}
            onSelect={setSelectedCode}
            className="h-12 w-2/3 rounded-t-full border-x-2 border-t-2"
          />

          <div className="flex h-48 w-full items-center justify-between gap-4">
            <Sector
              code="vip-barat"
              label={t("sectors.vipBarat")}
              zones={zones}
              selected={selectedCode}
              onSelect={setSelectedCode}
              className="h-full w-12 rounded-l-2xl border-y-2 border-l-2 [writing-mode:vertical-rl] [text-orientation:mixed]"
            />

            {/* Lapangan */}
            <div className="relative h-full grow rounded-sm bg-linear-to-b from-emerald-900/60 to-emerald-950/60 opacity-80">
              <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-white/30" />
              <div className="absolute left-1/2 top-1/2 size-12 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/30" />
            </div>

            <Sector
              code="ekonomi-timur"
              label={t("sectors.timur")}
              zones={zones}
              selected={selectedCode}
              onSelect={setSelectedCode}
              className="h-full w-12 rounded-r-2xl border-y-2 border-r-2 [writing-mode:vertical-rl]"
            />
          </div>

          <Sector
            code="selatan"
            label={t("sectors.selatan")}
            zones={zones}
            selected={selectedCode}
            onSelect={setSelectedCode}
            className="h-12 w-2/3 rounded-b-full border-x-2 border-b-2"
          />
        </div>
      </section>

      <section className="flex flex-wrap justify-center gap-4 border-b border-outline-variant/10 pb-6">
        <Legend className="border-outline-variant/30 bg-surface-container-high" label={t("legend.tersedia")} />
        <Legend className="border-primary bg-primary-container/20" label={t("legend.dipilih")} />
        <Legend className="border-error/40 bg-error/20" label={t("legend.penuh")} />
      </section>

      {/* Ringkasan sektor terpilih */}
      {zone && (
        <section className="orange-bloom rounded-2xl border border-white/10 bg-surface-elevated p-5">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-headline text-headline-md text-on-surface">{zone.name}</h3>
              <p className="mt-1 flex items-center gap-1.5 text-body-sm text-on-surface-variant">
                <Armchair className="size-4" aria-hidden />
                {soldOut ? t("habisTerjual") : t("tersediaKursi", { n: zoneAvailable })}
              </p>
            </div>
            <div className="text-right">
              <span className="block font-label text-label-caps uppercase text-primary">
                {t("harga")}
              </span>
              <span className="font-headline text-headline-lg text-on-surface">
                {formatRupiah(zone.price)}
              </span>
            </div>
          </div>

          <div className="mt-stack-md flex items-center justify-between rounded-xl border border-white/5 bg-surface-overlay p-3">
            <span className="flex items-center gap-2 text-body-sm text-on-surface-variant">
              <Star className="size-4 text-warning" aria-hidden />
              {t("dapatkanPoin", { n: Math.round(zone.price / 1000) })}
            </span>
            <span className="rounded bg-surface-container-low px-2 py-1 font-label text-label-caps text-on-surface">
              {t("goldMember")}
            </span>
          </div>

          <Button
            href={`/tiket/${fixtureId}/kursi?zona=${zone.code}`}
            size="lg"
            full
            className="mt-stack-md"
            {...(soldOut ? { "aria-disabled": true } : {})}
          >
            {soldOut ? t("sektorHabis") : t("konfirmasiSektor")}
            {!soldOut && <ArrowRight className="size-5" aria-hidden />}
          </Button>
        </section>
      )}
    </>
  );
}

function Sector({
  code,
  label,
  zones,
  selected,
  onSelect,
  className,
}: {
  code: (typeof SECTOR_POSITIONS)[number];
  label: string;
  zones: TicketingZone[];
  selected: string;
  onSelect: (code: string) => void;
  className: string;
}) {
  const zone = zones.find((z) => z.code === code);
  const isSelected = selected === code;
  const soldOut = zone ? availableCount(zone) === 0 : false;
  const belumTersedia = !zone;

  return (
    <button
      onClick={() => zone && onSelect(code)}
      disabled={belumTersedia}
      aria-pressed={isSelected}
      className={cn(
        "grid place-items-center font-label text-label-caps uppercase transition-all duration-300 active:scale-95",
        className,
        belumTersedia
          ? "cursor-not-allowed border-outline-variant/10 bg-surface-container-lowest text-on-surface-variant/20"
          : soldOut
            ? "border-error/40 bg-error/10 text-error/70"
            : isSelected
              ? "border-primary bg-primary-container/20 text-primary"
              : "border-outline-variant/30 bg-surface-container-high text-on-surface-variant hover:border-primary hover:text-primary",
      )}
    >
      {label}
    </button>
  );
}

function Legend({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-2">
      <span className={cn("size-4 rounded-sm border", className)} />
      <span className="text-body-sm text-on-surface-variant">{label}</span>
    </span>
  );
}
